import app from 'flarum/forum/app';

/** Rank movement since about a week ago: "↑2" (up) or "↓1" (down); nothing when unchanged or unknown */
const moveBadge = g => g && g.move ? m("span", {
  className: 'VGov-move ' + (g.move > 0 ? 'is-up' : 'is-down'),
  title: (g.move > 0 ? 'Up ' : 'Down ') + Math.abs(g.move) + ' since last week'
}, (g.move > 0 ? '↑' : '↓') + Math.abs(g.move)) : null;
import Component from 'flarum/common/Component';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import { param, rankTier } from '../../common/community';
import { setGovernorsHero } from '../../common/governorsHero';
import { rankingText } from './RankingAbout';
const ALL = {
  zone: '',
  party: ''
};
const MAX_YEARS = 8; // two four-year terms
const SORTS = [['rank', 'Rank: best first'], ['rankLow', 'Rank: lowest first'], ['igrPc', 'IGR per person'], ['population', 'Population'], ['years', 'Years in power'], ['age', 'Age'], ['name', 'Name']];

// ---------- small helpers ----------
const yearsBetween = (from, to = new Date()) => from ? Math.floor((to - new Date(from)) / (365.25 * 864e5)) : null;
const age = g => yearsBetween(g.birthDate);
const served = g => yearsBetween(g.since);
const left = g => served(g) === null || g.role !== 'Governor' ? null : Math.max(0, MAX_YEARS - served(g));
const millions = v => v ? (v / 1e6).toFixed(1).replace(/\.0$/, '') + 'M' : '—';
const initials = name => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
const longDate = d => d ? new Date(d).toLocaleDateString('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric'
}) : '—';
const yearsLabel = g => served(g) === null ? '—' : left(g) === null ? served(g) + ' yrs' : served(g) + ' / ' + left(g) + ' left';
const naira = v => v === null || v === undefined ? null : '₦' + Math.round(v).toLocaleString('en-US');
const pct = v => v === null || v === undefined ? null : Math.round(v * 100) + '%';
const debtYears = v => v === null || v === undefined ? null : v.toFixed(1) + ' yrs';
const scoreLabel = g => g.score !== null && g.score !== undefined ? Math.round(g.score) + '/100' : g.ranked === false ? 'Not ranked' : '—';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "2025" / "2025-12" / "2025-Q4" -> "2025" / "Dec 2025" / "Q4 2025" */
const periodLabel = p => {
  const s = String(p || '');
  let m1 = s.match(/^(\d{4})-(\d{2})/);
  if (m1) return MONTHS[Number(m1[2]) - 1] + ' ' + m1[1];
  m1 = s.match(/^(\d{4})-?Q([1-4])$/i);
  if (m1) return 'Q' + m1[2] + ' ' + m1[1];
  return s;
};

/** What each ranking measure says for a state, in words */
const MEASURES = {
  igr_pc: ['IGR per person', g => g.igrPc !== null && g.igrPc !== undefined ? naira(g.igrPc) + ' a year' : null],
  igr_faac: ['Own revenue vs FAAC', g => g.igrFaac !== null && g.igrFaac !== undefined ? 'IGR is ' + pct(g.igrFaac) + ' of its FAAC' : null],
  debt_igr: ['Debt burden', g => g.debtIgr !== null && g.debtIgr !== undefined ? 'Debt equals ' + debtYears(g.debtIgr) + ' of IGR' : null],
  igr: ['Total IGR', g => g.igr ? '₦' + Number(g.igr.value).toFixed(1) + 'bn' : null],
  population: ['Population', g => g.population ? millions(g.population) : null],
  gdp: ['GDP', g => g.gdp ? '₦' + Number(g.gdp.value).toFixed(2) + 'trn' : null],
  gdp_faac: ['GDP ÷ FAAC', g => g.ratio ? g.ratio.toFixed(1) + '×' : null]
};

/**
 * Governor photo (a versioned address the server gives only when the photo exists), falling back to initials.
 * Failures are remembered per photo address: Mithril reuses these boxes when the list
 * changes, so a per-box flag would show initials for a state that does have a photo.
 */
const brokenPhotos = new Set();
class Photo extends Component {
  view() {
    const g = this.attrs.g;
    const broken = !g.photo || brokenPhotos.has(g.photo);
    return m("div", {
      className: 'VGov-photo' + (this.attrs.big ? ' is-big' : '') + rankTier(g.rank, g.of)
    }, broken ? m("span", {
      className: "VGov-initials"
    }, initials(g.name)) : m("img", {
      src: g.photo,
      alt: g.name || g.state,
      loading: "lazy",
      onerror: () => {
        brokenPhotos.add(g.photo);
        m.redraw();
      }
    }), this.attrs.big && g.rank ? m("span", {
      className: "VGov-rankBadge"
    }, "#", g.rank, moveBadge(g)) : null);
  }
}

/**
 * /community/governors - the 36 states' governors, top 10 by default; search, zone and party filters, one sort.
 * Click a governor for how their score is made, their background, and links. The FCT is listed, not ranked.
 */
export default class CommunityGovernors extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.list = null;
    this.query = '';
    this.f = {
      ...ALL
    };
    this.sort = 'rank';
    this.mode = param('view') === 'table' ? 'table' : 'cards';
    this.tsort = {
      key: 'rank',
      dir: 1
    };
    this.showAll = false; // cards and table start at the top 10; "Show all" lists every governor
    this.showFilters = false; // phones: filters fold behind a button
    this.showMethod = false;
    this.open = param('state') || null; // arriving from the leadership panel opens that state's details
    this.votes = {
      counts: {},
      mine: {}
    };
    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/voucha-community/governors',
      errorHandler: () => {}
    }).then(r => {
      this.list = r.governors;
      this.ranking = r.ranking;
      setGovernorsHero(r.hero); // the banner photo, only if it exists
      if (this.open) {
        const g = this.list.find(x => x.slug === this.open);
        if (g) this.query = g.state;
      }
    }).catch(() => this.list = []).finally(() => m.redraw());
    this.loadVotes();
  }
  loadVotes() {
    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/voucha-community/rank-votes',
      errorHandler: () => {}
    }).then(r => {
      this.votes = {
        counts: r.counts || {},
        mine: r.mine || {}
      };
    }).catch(() => {}).finally(() => m.redraw());
  }
  vote(g, value) {
    if (!app.session.user) {
      try {
        const p = app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/SignUpModal'));
        if (p && p.catch) p.catch(() => {});
      } catch (e) {}
      return;
    }
    const mine = this.votes.mine[g.stateId];
    const v = mine === value ? 0 : value; // tapping your vote again takes it back
    app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/voucha-community/rank-votes',
      body: {
        state: g.stateId,
        vote: v
      }
    }).then(r => {
      this.votes = {
        counts: r.counts || {},
        mine: r.mine || {}
      };
    }).catch(() => {}).finally(() => m.redraw());
  }
  filtered() {
    const q = this.query.trim().toLowerCase();
    const f = this.f;
    const rows = this.list.filter(g => {
      if (q && ![g.state, g.name, g.party, g.zone, g.capital].join(' ').toLowerCase().includes(q)) return false;
      if (f.zone && g.zone !== f.zone) return false;
      if (f.party && g.party !== f.party) return false;
      return true;
    });
    const by = {
      rank: (a, b) => (a.rank ?? 999) - (b.rank ?? 999),
      rankLow: (a, b) => (b.rank ?? -1) - (a.rank ?? -1),
      igrPc: (a, b) => (b.igrPc ?? -1) - (a.igrPc ?? -1),
      population: (a, b) => (b.population || 0) - (a.population || 0),
      years: (a, b) => (served(b) ?? -1) - (served(a) ?? -1),
      age: (a, b) => (age(b) ?? -1) - (age(a) ?? -1),
      name: (a, b) => (a.name || '').localeCompare(b.name || '')
    };
    return rows.sort(by[this.sort] || by.rank);
  }
  isDefault() {
    return !this.query.trim() && Object.values(this.f).every(v => !v);
  }
  view() {
    if (!this.list) return m(LoadingIndicator, null);
    if (this.mode === 'table') return this.withControls(this.table(this.filtered()));
    let rows = this.filtered();
    const defaultView = this.isDefault();
    const byRank = this.sort === 'rank' || this.sort === 'rankLow';
    const capCards = defaultView && byRank && !this.showAll;
    const lowest = this.sort === 'rankLow';
    const unranked = this.list.filter(g => g.ranked === false);
    if (defaultView) rows = rows.filter(g => g.ranked !== false); // the FCT gets its own note below
    if (capCards) rows = rows.filter(g => g.rank).slice(0, 10); // the top 10, or the bottom 10 (lowest first)
    const [featured, ...others] = rows;
    return this.withControls(m("div", null, !featured ? m("p", {
      className: "VGov-empty"
    }, "No governors match. Try another search or reset the filters.") : [m("h3", {
      className: "VGov-heading"
    }, capCards ? lowest ? 'Bottom 10: lowest ranked first' : 'Top 10' : defaultView ? 'All ' + rows.length + ' governors' : 'Search result'), this.featured(featured), this.open === featured.slug ? this.details(featured) : null, others.length && !capCards ? m("h3", {
      className: "VGov-heading VGov-othersHead"
    }, "Other governors") : null,
    // the governor you open grows to the big card, with its details under it
    others.length ? m("div", {
      className: "VGov-list"
    }, others.map(g => m("div", {
      key: g.slug,
      className: this.open === g.slug ? 'VGov-opened' : ''
    }, this.open === g.slug ? [this.featured(g), this.details(g)] : this.row(g)))) : null, capCards ? m("div", {
      className: "VGov-showMore"
    }, m("button", {
      className: "Button",
      onclick: () => this.showAll = true
    }, "Show all ", this.list.filter(g => g.ranked !== false).length, " governors"), m("span", null, "or search above")) : null, defaultView && byRank && this.showAll ? m("div", {
      className: "VGov-showMore"
    }, m("button", {
      className: "Button",
      onclick: () => this.showAll = false
    }, "Show the ", lowest ? 'bottom' : 'top', " 10")) : null], defaultView && unranked.length ? m("div", {
      className: "VGov-unranked"
    }, unranked.map(g => m("div", {
      key: g.slug
    }, this.open === g.slug ? m("div", {
      className: "VGov-opened"
    }, this.featured(g)) : m("div", {
      className: 'VGov-row is-unranked' + (this.open === g.slug ? ' is-open' : ''),
      onclick: () => this.toggle(g),
      role: "button",
      tabindex: "0"
    }, m(Photo, {
      g: g
    }), m("div", {
      className: "VGov-rowName"
    }, m("strong", null, g.name || 'Not yet recorded'), m("span", {
      className: "VGov-state"
    }, g.role, ", ", g.state, g.party ? m("span", {
      className: "VGov-party"
    }, g.party) : null)), m("span", {
      className: "VGov-unrankedNote"
    }, "Not ranked: ", this.ranking && this.ranking.unranked || 'the FCT has no governor and is funded differently.'), m("i", {
      className: 'fas fa-chevron-' + (this.open === g.slug ? 'up' : 'right') + ' VGov-chevron',
      "aria-hidden": "true"
    })), this.open === g.slug ? this.details(g) : null))) : null));
  }

  /** Table view: every figure for every matching governor; click a column to sort. */
  table(rows) {
    const bn = v => v === null || v === undefined ? null : '₦' + Math.round(v).toLocaleString('en-US') + 'bn';
    const cols = [['rank', 'Rank', g => g.rank, g => g.rank ? '#' + g.rank : g.ranked === false ? 'Not ranked' : null], ['score', 'Score', g => g.score ?? -1, g => g.score !== null && g.score !== undefined ? Math.round(g.score) + '/100' : null], ['name', 'Governor', g => g.name || '', g => g.name], ['state', 'State', g => g.state, g => g.state], ['party', 'Party', g => g.party || '', g => g.party], ['zone', 'Region', g => g.zone, g => g.zone], ['since', 'In office since', g => g.since || '', g => g.since ? new Date(g.since).getFullYear() : null], ['years', 'Years', g => served(g) ?? -1, g => yearsLabel(g)], ['age', 'Age', g => age(g) ?? -1, g => age(g)], ['population', 'Population', g => g.population ?? -1, g => g.population ? Math.round(g.population).toLocaleString('en-US') : null], ['igrPc', 'IGR per person', g => g.igrPc ?? -1, g => naira(g.igrPc)], ['igrFaac', 'Own revenue vs FAAC', g => g.igrFaac ?? -1, g => pct(g.igrFaac)], ['debtIgr', 'Debt, years of IGR', g => g.debtIgr ?? 1e9, g => debtYears(g.debtIgr)], ['gdp', 'GDP', g => g.gdp ? g.gdp.value : -1, g => g.gdp ? '₦' + g.gdp.value.toFixed(2) + 'trn' : null], ['faac', 'FAAC, 12 months', g => g.faac12 ?? -1, g => bn(g.faac12)], ['ratio', 'GDP ÷ FAAC', g => g.ratio ?? -1, g => g.ratio ? g.ratio.toFixed(1) + '×' : null], ['debtDom', 'Debt, domestic', g => g.debtDomestic ? g.debtDomestic.value : -1, g => g.debtDomestic ? bn(g.debtDomestic.value) : null], ['debtExt', 'Debt, external', g => g.debtExternal ? g.debtExternal.value : -1, g => g.debtExternal ? '$' + Math.round(g.debtExternal.value).toLocaleString('en-US') + 'm' : null], ['rulers', 'Traditional rulers', g => g.rulers ?? -1, g => g.rulers], ['lgas', 'LGAs', g => g.lgaCount ?? -1, g => g.lgaCount], ['capital', 'Capital', g => g.capital || '', g => g.capital], ['predecessor', 'Predecessor', g => g.predecessor ? g.predecessor.name : '', g => g.predecessor ? g.predecessor.name : null]];
    const t = this.tsort;
    const col = cols.find(c => c[0] === t.key) || cols[0];
    const sorted = rows.slice().sort((a, b) => {
      const x = col[2](a);
      const y = col[2](b);
      if (t.key === 'rank') return ((x ?? 999) - (y ?? 999)) * t.dir;
      return (typeof x === 'number' ? x - y : String(x).localeCompare(String(y))) * t.dir;
    });
    const sortBy = key => this.tsort = {
      key,
      dir: t.key === key ? -t.dir : ['rank', 'name', 'state', 'debtIgr'].includes(key) ? 1 : -1
    };
    // Top 10 of the current sort, unless searching / filtering or "Show all" was clicked
    const capTable = this.isDefault() && !this.showAll && sorted.length > 10;
    return m("div", null, m("div", {
      className: "VGov-tableWrap"
    }, m("table", {
      className: "VGov-table"
    }, m("thead", null, m("tr", null, cols.map(([key, label]) => m("th", {
      className: t.key === key ? 'is-sorted' : '',
      onclick: () => sortBy(key)
    }, label, " ", t.key === key ? m("i", {
      className: 'fas fa-caret-' + (t.dir > 0 ? 'up' : 'down')
    }) : null)))), m("tbody", null, (capTable ? sorted.slice(0, 10) : sorted).map(g => m("tr", null, cols.map(([key,,, show]) => {
      const v = show(g);
      return m("td", {
        className: key === 'name' ? 'VGov-tName' : ''
      }, v === null || v === undefined || v === '' ? m("span", {
        className: "VGov-na"
      }, "\u2014") : v);
    })))))), capTable ? m("div", {
      className: "VGov-showMore"
    }, m("button", {
      className: "Button",
      onclick: () => this.showAll = true
    }, "Show all ", sorted.length, " governors"), m("span", null, "or search above")) : this.isDefault() && this.showAll && sorted.length > 10 ? m("div", {
      className: "VGov-showMore"
    }, m("button", {
      className: "Button",
      onclick: () => this.showAll = false
    }, "Show top 10")) : null, m("p", {
      className: "VGov-tableFoot"
    }, sorted.length, " of ", this.list.length, " governors \xB7 ranked by ", (this.ranking || {}).measure, " \xB7", ' ', m("a", {
      href: "#",
      onclick: e => {
        e.preventDefault();
        const esc = v => /[",\n]/.test(String(v ?? '')) ? '"' + String(v).replace(/"/g, '""') + '"' : String(v ?? '');
        const text = [cols.map(c => c[1]), ...sorted.map(g => cols.map(c => show(c, g)))].map(r => r.map(esc).join(',')).join('\n');
        function show(c, g) {
          const v = c[3](g);
          return v === null || v === undefined ? '' : v;
        }
        const url = URL.createObjectURL(new Blob(['\ufeff' + text], {
          type: 'text/csv;charset=utf-8'
        }));
        const a = document.createElement('a');
        a.href = url;
        a.download = 'nigeria-governors.csv';
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    }, "Download CSV")));
  }

  /** Search, filters, sort and the Cards | Table switch: shared by both views. */
  withControls(content) {
    return m("div", {
      className: "VGov"
    }, this.controls(), this.method(), content);
  }
  controls() {
    const options = key => Array.from(new Set(this.list.map(g => g[key]).filter(Boolean))).sort();
    const select = (key, label, opts) => m("select", {
      className: "FormControl VGov-filter",
      value: this.f[key],
      "aria-label": label,
      onchange: e => this.f[key] = e.target.value
    }, m("option", {
      value: ""
    }, label), opts.map(([v, t]) => m("option", {
      value: v
    }, t)));
    const active = Object.values(this.f).filter(Boolean).length + (this.sort !== 'rank' ? 1 : 0);
    return [m("div", {
      className: "VGov-search"
    }, m("i", {
      className: "fas fa-search",
      "aria-hidden": "true"
    }), m("input", {
      className: "FormControl",
      placeholder: "Search by state, governor, party or region",
      value: this.query,
      oninput: e => this.query = e.target.value
    }), this.query ? m("button", {
      className: "VGov-clear",
      "aria-label": "Clear search",
      onclick: () => this.query = ''
    }, m("i", {
      className: "fas fa-times"
    })) : null), m("div", {
      className: "VGov-bar"
    }, m("button", {
      className: 'Button VGov-filtersToggle' + (this.showFilters ? ' is-on' : ''),
      onclick: () => this.showFilters = !this.showFilters,
      "aria-expanded": this.showFilters ? 'true' : 'false'
    }, m("i", {
      className: "fas fa-sliders-h"
    }), " Filters", active ? ' (' + active + ')' : ''), m("div", {
      className: 'VGov-filters' + (this.showFilters ? ' is-open' : '')
    }, select('zone', 'All zones', options('zone').map(z => [z, z])), select('party', 'All parties', options('party').map(p => [p, p])), this.mode === 'cards' ? m("label", {
      className: "VGov-sort"
    }, m("span", null, "Sort"), m("select", {
      className: "FormControl VGov-filter",
      value: this.sort,
      onchange: e => {
        this.sort = e.target.value;
        this.open = null;
      }
    }, SORTS.map(([v, t]) => m("option", {
      value: v
    }, t)))) : null, active || this.query ? m("button", {
      className: "VGov-reset",
      onclick: () => {
        this.f = {
          ...ALL
        };
        this.query = '';
        this.sort = 'rank';
        this.open = null;
      }
    }, "Reset") : null), m("div", {
      className: "VGov-mode",
      role: "tablist"
    }, m("button", {
      className: this.mode === 'cards' ? 'is-active' : '',
      onclick: () => this.mode = 'cards'
    }, m("i", {
      className: "fas fa-th-large"
    }), " Cards"), m("button", {
      className: this.mode === 'table' ? 'is-active' : '',
      onclick: () => this.mode = 'table'
    }, m("i", {
      className: "fas fa-table"
    }), " Table")))];
  }

  /** How the ranking works and how fresh its figures are (phones don't have the sidebar's "About this ranking") */
  method() {
    const r = this.ranking || {};
    if (r.key !== 'composite') return null;
    const periods = (r.periods || []).filter(p => p.period);
    return m("div", {
      className: "VGov-method"
    }, m("p", null, m("strong", null, "Scored out of 100, comparing the ", r.states || 36, " states."), ' ', periods.length ? 'Figures: ' + periods.map(p => p.label + ' ' + (p.upTo ? 'to ' : '') + periodLabel(p.period) + ' (' + p.source + ')').join(' · ') + '.' : null, ' ', m("button", {
      className: "VGov-methodToggle",
      onclick: () => this.showMethod = !this.showMethod,
      "aria-expanded": this.showMethod ? 'true' : 'false'
    }, "How it's calculated ", m("i", {
      className: 'fas fa-chevron-' + (this.showMethod ? 'up' : 'down')
    }))), this.showMethod ? m("div", {
      className: "VGov-methodBody"
    }, m("p", null, rankingText(r).replace(/^States are ranked out of 100 using \w+ indicators?: /, 'Measures and weights: ').replace(/ Scores reflect performance compared to other states\.$/, '')), m("p", null, r.unranked), m("p", null, "Each measure places a state between 0 (lowest) and 100 (highest); for debt, less debt scores higher. The score is their weighted average, and arrows show the move since about a week ago.")) : null);
  }
  toggle(g) {
    this.open = this.open === g.slug ? null : g.slug;
    if (!this.open) return;
    // bring the enlarged card into view (it can push below the fold)
    setTimeout(() => {
      const el = document.querySelector('.VGov-opened .VGov-featured');
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      if (top < 70 || top > window.innerHeight * 0.6) window.scrollTo({
        top: top + window.pageYOffset - 80,
        behavior: 'smooth'
      });
    }, 30);
  }
  stat(icon, value, label, extra = false) {
    return m("div", {
      className: 'VGov-stat' + (extra ? ' is-extra' : '')
    }, m("i", {
      className: icon,
      "aria-hidden": "true"
    }), m("span", null, m("strong", null, value ?? '—'), m("small", null, label)));
  }
  stateLine(g) {
    return m("span", {
      className: "VGov-state"
    }, g.state === 'FCT (Abuja)' ? 'FCT (Abuja)' : g.state + ' State', g.party ? m("span", {
      className: "VGov-party"
    }, g.party) : null);
  }

  /** The figures behind the ranking, on every card */
  rankStats(g, short) {
    return [this.stat('fas fa-star', scoreLabel(g), 'Score'), this.stat('fas fa-coins', naira(g.igrPc), short ? 'IGR/person' : 'IGR per person'), this.stat('fas fa-hand-holding-usd', pct(g.igrFaac), short ? 'vs FAAC' : 'Own revenue vs FAAC', true), this.stat('fas fa-file-invoice-dollar', debtYears(g.debtIgr), short ? 'Debt' : 'Debt, in years of IGR'), this.stat('far fa-calendar-alt', yearsLabel(g), 'Years in power', true)];
  }
  featured(g) {
    return m("div", {
      className: 'VGov-featured' + rankTier(g.rank, g.of) + (this.open === g.slug ? ' is-open' : ''),
      onclick: () => this.toggle(g),
      role: "button",
      tabindex: "0"
    }, m(Photo, {
      g: g,
      big: true
    }), m("div", {
      className: "VGov-featuredBody"
    }, m("div", {
      className: "VGov-featuredHead"
    }, m("div", null, m("h2", null, g.name || g.role + ': not yet recorded'), this.stateLine(g)), m("span", {
      className: "VGov-toggle"
    }, this.open === g.slug ? 'Hide details' : g.rank ? 'Why this rank' : 'Details', " ", m("i", {
      className: 'fas fa-chevron-' + (this.open === g.slug ? 'up' : 'down')
    }))), m("div", {
      className: "VGov-stats"
    }, this.rankStats(g, false))));
  }
  row(g) {
    return m("div", {
      className: 'VGov-row' + rankTier(g.rank, g.of) + (this.open === g.slug ? ' is-open' : ''),
      onclick: () => this.toggle(g),
      role: "button",
      tabindex: "0"
    }, m("span", {
      className: "VGov-rowRank"
    }, g.rank ? '#' + g.rank : '—', moveBadge(g)), m(Photo, {
      g: g
    }), m("div", {
      className: "VGov-rowName"
    }, m("strong", null, g.name || 'Not yet recorded'), this.stateLine(g)), m("div", {
      className: "VGov-rowStats"
    }, this.rankStats(g, true)), m("i", {
      className: 'fas fa-chevron-' + (this.open === g.slug ? 'up' : 'right') + ' VGov-chevron',
      "aria-hidden": "true"
    }));
  }
  share(g) {
    const url = app.forum.attribute('baseUrl') + '/community/governors?state=' + encodeURIComponent(g.slug);
    const text = g.rank ? g.state + ' is ranked #' + g.rank + ' of ' + g.of + ' states on Voucha (' + scoreLabel(g) + '). Where does your state rank?' : g.state + ' on Voucha: governors and state figures.';
    if (navigator.share) {
      navigator.share({
        title: 'Nigeria\'s governors ranked',
        text,
        url
      }).catch(() => {});
    } else {
      window.open('https://wa.me/?text=' + encodeURIComponent(text + ' ' + url), '_blank', 'noopener');
    }
  }

  /** "Do you agree with this rank?" One tap; members only */
  agree(g) {
    if (!g.rank) return null;
    const c = this.votes.counts[g.stateId] || {
      agree: 0,
      disagree: 0
    };
    const mine = this.votes.mine[g.stateId];
    const total = c.agree + c.disagree;
    return m("div", {
      className: "VGov-agree"
    }, m("span", null, "Do you agree with #", g.rank, " for ", g.state, "?"), m("button", {
      className: 'Button' + (mine === 1 ? ' is-on' : ''),
      onclick: () => this.vote(g, 1)
    }, m("i", {
      className: "far fa-thumbs-up"
    }), " Agree"), m("button", {
      className: 'Button' + (mine === -1 ? ' is-on' : ''),
      onclick: () => this.vote(g, -1)
    }, m("i", {
      className: "far fa-thumbs-down"
    }), " Disagree"), total ? m("small", null, total >= 5 ? Math.round(100 * c.agree / total) + '% agree · ' : '', total, " ", total === 1 ? 'vote' : 'votes') : null);
  }
  details(g) {
    const r = this.ranking || {};
    // Only rows that have a value: everything else is in the Table view
    const item = (label, value) => value === null || value === undefined || value === '' ? null : m("div", {
      className: "VGov-item"
    }, m("small", null, label), m("span", null, value));
    const background = [item('Born', g.birthDate ? longDate(g.birthDate) + ' (age ' + age(g) + ')' : null), item('Education', g.education), item('Previous roles', g.previousRoles)].filter(Boolean);
    const weights = r.weights || {};
    const used = (r.used || []).filter(k => MEASURES[k]);
    const parts = g.parts || {};
    return m("div", {
      className: "VGov-details",
      onclick: e => e.stopPropagation()
    }, g.rank && used.length ? m("section", {
      className: "VGov-why"
    }, m("h4", null, m("i", {
      className: "fas fa-balance-scale"
    }), " Why #", g.rank), m("p", {
      className: "VGov-whyLead"
    }, "Score ", scoreLabel(g), ", #", g.rank, " of ", g.of, " states", g.move ? ' · ' + (g.move > 0 ? '↑' : '↓') + Math.abs(g.move) + ' since last week' : '', "."), used.map(k => {
      const p = parts[k];
      return m("div", {
        className: "VGov-part"
      }, m("div", {
        className: "VGov-partHead"
      }, m("strong", null, MEASURES[k][0]), m("small", null, weights[k], "% of the score")), m("div", {
        className: "VGov-partBar",
        title: p !== undefined ? 'Better than ' + Math.round(p) + '% of states' : 'No figure'
      }, m("span", {
        style: {
          width: (p !== undefined ? Math.max(2, p) : 0) + '%'
        }
      })), m("small", null, MEASURES[k][1](g) || 'No figure yet', p !== undefined ? ' · better than ' + Math.round(p) + '% of states' : ''));
    })) : null, m("section", null, m("h4", null, m("i", {
      className: "fas fa-star"
    }), " At a glance"), g.ranked === false ? item('Ranking', 'Not ranked. ' + (r.unranked || '')) : null, item(g.role + ' since', g.since ? longDate(g.since) : null), item('Population', g.population ? Math.round(g.population).toLocaleString('en-US') : null), item('IGR', g.igr ? '₦' + Number(g.igr.value).toFixed(1) + 'bn' + (g.igr.period ? ' (' + periodLabel(g.igr.period) + ')' : '') : null), item('GDP', g.gdp ? '₦' + Number(g.gdp.value).toFixed(2) + 'trn' + (g.gdp.period ? ' (' + periodLabel(g.gdp.period) + ')' : '') : null), item('Traditional rulers', g.rulers), item('Age', age(g))), background.length || g.bio ? m("section", null, m("h4", null, m("i", {
      className: "fas fa-user"
    }), " Background"), background, g.bio ? m("p", {
      className: "VGov-bio"
    }, g.bio) : null) : null, this.agree(g), m("div", {
      className: "VGov-links"
    }, m("a", {
      href: '/data-hub/states/' + g.slug,
      onclick: e => {
        if (e.ctrlKey || e.metaKey) return;
        e.preventDefault();
        m.route.set('/data-hub/states/' + g.slug);
      }
    }, g.state, " profile ", m("i", {
      className: "fas fa-arrow-right"
    })), g.tagSlug ? m("a", {
      href: app.route('tag', {
        tags: g.tagSlug
      })
    }, g.state, " discussions \u2192") : null, m("a", {
      href: "#",
      onclick: e => {
        e.preventDefault();
        this.mode = 'table';
        this.query = g.state;
      }
    }, "All figures ", m("i", {
      className: "fas fa-table"
    })), m("a", {
      href: "#",
      onclick: e => {
        e.preventDefault();
        this.share(g);
      }
    }, "Share ", m("i", {
      className: "fas fa-share-alt"
    })), g.source ? m("a", {
      href: g.source,
      target: "_blank",
      rel: "noopener"
    }, "Source ", m("i", {
      className: "fas fa-external-link-alt"
    })) : null));
  }
}
flarum.reg.add('voucha-community', 'forum/components/CommunityGovernors', CommunityGovernors);